import type { Deal } from '@/models/registry';
import type { Address } from '@/models/types.ts';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function generateOptimisticId({ prefix }: { prefix?: string } = {}) {
  return `${prefix ? `${prefix}-` : ''}${Math.random().toString(36).substring(2, 15)}`;
}

export function pluralize(quantity: number, singular: string, plural: string) {
  return quantity == 1 ? singular : plural;
}

/**
 * Format bytes to a human-readable format
 * @param bytes - The number of bytes
 * @param decimals - The number of decimal places to show
 * @returns A formatted string with the appropriate unit (B, KB, MB, GB, TB)
 */
export function formatBytes(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export const randomNumberBetween = (
  min: number,
  max: number,
  decimals: number = 0,
) => {
  return (Math.floor(Math.random() * (max - min + 1)) + min).toFixed(decimals);
};

export const jwtDecode = (token: string) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );
  return JSON.parse(jsonPayload);
};

// example: "deal_stage" -> "Deal Stage"
// example: "medium" -> "Medium"
export const snakeCaseToTitleCase = (str: string) => {
  return str.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatNumberToAddCommas = (number: number) => {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const formatAddress = (address: Address | null | undefined): string => {
  if (!address) return '';

  const parts = [
    address.address_line_1,
    address.address_line_2,
    address.city,
    [address.state, address.postal_code].filter(Boolean).join(' '),
  ]
    .map((part) => part?.trim())
    .filter(Boolean);

  return parts.join(', ');
};

export const isDealActive = (deal: Deal | null | undefined): boolean => {
  if (!deal) return false;
  return deal.status !== 'cancelled' && deal.status !== 'done';
};

export const makeCRMURL = (id: number): string => {
  return `https://vinsolutions.app.coxautoinc.com/vinconnect/#/CarDashboard/Pages/LeadManagement/ActiveLeadsLayout.aspx?SelectedTab=t_ILM&leftpaneframe=ActiveLeads_NewLeadList.aspx&rightpaneframe=../CRM/CustomerDashboard.aspx?GlobalCustomerID=${id}`;
};

export const makeVinSolutionsSearchURL = (searchTerm: string): string => {
  const encoded = encodeURIComponent(searchTerm);
  return `https://vinsolutions.app.coxautoinc.com/vinconnect/#/Cardashboard/Pages/LeadManagement/ActiveLeadsLayout.aspx?NoMenu=true&SelectedTab=t_ILM&rightpaneframe=HIDE&leftpaneframe=AdvancedSearch2.aspx?searchdata=${encoded}`;
};

type VAutoAppraiseParams = {
  vin: string;
  odometer?: number | null;
  firstName?: string;
  lastName?: string;
  email?: string;
  zip?: string;
};

export const makeVAutoAppraiseByVinURL = (
  params: VAutoAppraiseParams,
): string => {
  const { vin, odometer, firstName, lastName, email, zip } = params;
  const initialData: Record<string, string> = {
    Vin: vin,
    Source: 'LeaseReturn',
    SaveAsInventory: 'false',
  };
  // Best-guess vAuto InitialData keys — vAuto ignores unrecognized keys, so we
  // ship convention-based names and verify which actually populate in vAuto.
  if (odometer != null && odometer > 0) initialData.Odometer = String(odometer);
  if (firstName) initialData.FirstName = firstName;
  if (lastName) initialData.LastName = lastName;
  if (email) initialData.Email = email;
  // Coerce to String: `zip_code` can arrive as a number at runtime, which would
  // serialize as an unquoted JSON value and break vAuto. Every InitialData value
  // must be a quoted string.
  if (zip) initialData.PostalCode = String(zip);
  // NOTE: Do NOT seed `TransportationCost` here. Pre-seeding a Disposition cost
  // group value via the deep-link breaks vAuto's on-screen appraisal calculation
  // (the recalc engine enters a bad state on a brand-new appraisal). Transport
  // cost must be entered manually in vAuto instead.
  return `https://provision.vauto.app.coxautoinc.com/Va/Appraisal/Default.aspx?new=true&InitialData=${encodeURIComponent(
    JSON.stringify(initialData),
  )}`;
};

export const roundLatOrLongTo3Decimals = (latOrLong: number): number => {
  return Math.round(latOrLong * 1000) / 1000;
};

/**
 * Simplifies URLs for display purposes, particularly for deal URLs
 * Converts /hub-name/deal/specific-deal-id to /hub-name/deal
 */
export const simplifyUrl = (url: string | undefined): string => {
  if (!url) return '-';

  const dealPattern = /^\/([^/]+)\/deal\/[^/]+(?:\/.*)?$/;
  const match = url.match(dealPattern);

  if (match) {
    return `/${match[1]}/deal`;
  }

  return url;
};

// extracts a union type from const object values (preferred alternative to TS enums)
export type ValueOf<T> = T[keyof T];

export const deferToNextFrame = (callback: () => void) => {
  requestAnimationFrame(callback);
};

// for task pages verbiage
export const getEntityTypeLabel = (type: string, count: number): string => {
  const typeNames: Record<string, { singular: string; plural: string }> = {
    deal: { singular: 'deal', plural: 'deals' },
    appraisal: { singular: 'appraisal', plural: 'appraisals' },
  };

  const typeConfig = typeNames[type] || {
    singular: 'entity',
    plural: 'entities',
  };
  return pluralize(count, typeConfig.singular, typeConfig.plural);
};

/**
 * true when the token has at least one digit and contains no letters.
 * - "123" => true
 * - "12-34" => true
 * - "abc" => false
 * - "a1" => false
 */
export const isDigitsOnlySearchToken = (token: string): boolean =>
  !/[a-z]/i.test(token) && /\d/.test(token);
